/**
 * StatsTabButtonContainer - Logic for stats tab button
 *
 * Handles tab button press events and callbacks
 * @remarks This is a Container component - contains logic and callbacks
 */

import { memo, useCallback } from "react";

import type { StatsTab, TabConfig } from "@/features/player-detail/types";

import StatsTabButtonView from "./StatsTabButtonView";

/**
 * Props for StatsTabButtonContainer component
 */
interface StatsTabButtonContainerProps {
  /** Tab configuration */
  readonly tab: TabConfig;
  /** Whether tab is currently active */
  readonly isActive: boolean;
  /** Whether dark mode is enabled */
  readonly isDark: boolean;
  /** Callback when tab changes */
  readonly onPress: (tabId: StatsTab) => void;
}

/**
 * StatsTabButtonContainer handles tab button logic
 * @param props - Container props
 * @param props.tab - Tab configuration object
 * @param props.isActive - Whether this tab is currently selected
 * @param props.isDark - Whether dark mode is enabled
 * @param props.onPress - Callback to invoke when button is pressed
 */
const StatsTabButtonContainer = memo(
  ({ tab, isActive, isDark, onPress }: StatsTabButtonContainerProps) => {
    const handlePress = useCallback(() => {
      onPress(tab.id);
    }, [onPress, tab.id]);

    return (
      <StatsTabButtonView
        label={tab.label}
        isActive={isActive}
        isDark={isDark}
        onPress={handlePress}
        testID={tab.testID}
      />
    );
  }
);

StatsTabButtonContainer.displayName = "StatsTabButtonContainer";

export default StatsTabButtonContainer;
